import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { TimelineEvent, GenerationGroup } from '../../types/timeline';
import { EventCard } from './EventCard';

interface GenerationGroupedViewProps {
  events: TimelineEvent[];
  onEventClick?: (event: TimelineEvent) => void;
  loading?: boolean;
  defaultExpanded?: boolean;
}

/**
 * GenerationGroupedView Component
 * Displays timeline events grouped by generation in expandable swim lanes
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const GenerationGroupedView: React.FC<GenerationGroupedViewProps> = ({
  events,
  onEventClick,
  loading = false,
  defaultExpanded = true,
}) => {
  // Group events by generation
  const generationGroups = useMemo(() => {
    if (!events || events.length === 0) return [];

    // Create a map of generation to events
    const groupMap = new Map<number | 'unknown', TimelineEvent[]>();

    events.forEach((event) => {
      const generation = event.generation !== undefined ? event.generation : 'unknown';

      if (!groupMap.has(generation)) {
        groupMap.set(generation, []);
      }
      groupMap.get(generation)!.push(event);
    });

    // Convert map to array and sort
    const groups: GenerationGroup[] = [];

    // Sort numeric generations
    const numericGenerations = Array.from(groupMap.keys())
      .filter((g) => g !== 'unknown')
      .sort((a, b) => (a as number) - (b as number));

    numericGenerations.forEach((generation) => {
      const generationEvents = groupMap.get(generation)!;

      // Sort events within generation by date
      const sortedEvents = [...generationEvents].sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      // Get unique people in this generation
      const people = new Set(generationEvents.map((e) => e.personId));

      groups.push({
        generation: generation as number,
        events: sortedEvents,
        label: `Generation ${(generation as number) + 1}`,
        people,
      });
    });

    // Add unknown generation at the end if exists
    if (groupMap.has('unknown')) {
      const unknownEvents = groupMap.get('unknown')!;
      const sortedEvents = [...unknownEvents].sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      // Get unique people in unknown generation
      const people = new Set(unknownEvents.map((e) => e.personId));

      groups.push({
        generation: -1,
        events: sortedEvents,
        label: 'Unknown Generation',
        people,
      });
    }

    return groups;
  }, [events]);

  // Get date range for a generation
  const getGenerationDateRange = (events: TimelineEvent[]): string => {
    const datesWithValues = events
      .filter((e) => e.date)
      .map((e) => new Date(e.date!).getFullYear())
      .sort((a, b) => a - b);

    if (datesWithValues.length === 0) return 'No dates';
    if (datesWithValues.length === 1) return datesWithValues[0].toString();

    const earliest = datesWithValues[0];
    const latest = datesWithValues[datesWithValues.length - 1];

    return `${earliest} - ${latest}`;
  };

  // Get unique people count for a generation
  const getUniquePeopleCount = (events: TimelineEvent[]): number => {
    const uniquePersonIds = new Set(events.map((e) => e.personId));
    return uniquePersonIds.size;
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading timeline events...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Empty state
  if (!generationGroups || generationGroups.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Events to Display
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters to see more events.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Events Grouped by Generation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {generationGroups.length} generation{generationGroups.length !== 1 ? 's' : ''} with{' '}
          {events.length} total event{events.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Generation Groups */}
      <Stack spacing={2}>
        {generationGroups.map((group, index) => {
          const peopleCount = getUniquePeopleCount(group.events);
          const dateRange = getGenerationDateRange(group.events);

          return (
            <Accordion
              key={`generation-${group.generation}-${index}`}
              defaultExpanded={defaultExpanded}
              elevation={2}
              sx={{
                '&:before': { display: 'none' },
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandIcon />}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 1.5,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    mr: 2,
                  }}
                >
                  {/* Generation Label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {group.label}
                    </Typography>
                    <Chip
                      label={`${group.events.length} event${group.events.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'inherit',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  {/* Metadata */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" />
                      <Typography variant="body2">
                        {peopleCount} {peopleCount === 1 ? 'person' : 'people'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {dateRange}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Stack spacing={2}>
                  {group.events.map((event, eventIndex) => (
                    <EventCard
                      key={`${event.id}-${eventIndex}`}
                      event={event}
                      onClick={onEventClick}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {/* Footer Summary */}
      <Box
        sx={{
          mt: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Viewing {generationGroups.length} generation{generationGroups.length !== 1 ? 's' : ''}{' '}
          with {events.length} total event{events.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default GenerationGroupedView;
